import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_CATEGORIES, URGENCIES } from "@/lib/constants";
import { Loader2, PlusCircle, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { RequestNumber, StatusTimeline } from "@/components/RequestTracker";

export default function RequestService() {
  const navigate = useNavigate();
  const { user, isViewer } = useAuth();
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ property_id: "", unit_number: "", service_category: "", problem_description: "", urgency: "Routine", requested_date: "", tenant_occupied: true, permission_to_enter: true, access_instructions: "", additional_notes: "" });
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target?.value ?? e });

  useEffect(() => { api.get("/properties").then(({ data }) => setProps(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.property_id) return toast.error("Select a property");
    if (!f.service_category) return toast.error("Select a service category");
    setLoading(true);
    try {
      const { data } = await api.post("/work-orders", f);
      const prop = props.find((p) => p.id === f.property_id);
      toast.success(`Request submitted — ${data.request_number || data.wo_number}`);
      setConfirm({ ...data, _propLabel: prop ? `${prop.name} — ${prop.city}, ${prop.state}` : "" });
    } catch (err) { toast.error("Failed to submit request"); }
    setLoading(false);
  };

  if (isViewer) {
    return (
      <DashboardLayout nav={getNav("client", user?.org_role)} title="Request Service">
        <div className="max-w-2xl text-center py-20 bg-white rounded-xl border border-slate-200">
          <Lock className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-slate-500" data-testid="viewer-readonly-request">Your account has read-only (Viewer) access. Ask an owner or manager to submit maintenance requests.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout nav={getNav("client", user?.org_role)} title="Request Service">
      {confirm ? (
        <div className="max-w-2xl" data-testid="request-confirmation">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-white" /></div>
              <div><h2 className="font-head text-xl font-semibold text-slate-900">Request Submitted</h2><p className="text-sm text-slate-500">We've received your maintenance request.</p></div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs uppercase tracking-wide text-slate-400">Request Number</span>
                <RequestNumber number={confirm.request_number} urgency={confirm.urgency} />
              </div>
              <Detail label="Property" value={confirm._propLabel} />
              <Detail label="Service Category" value={confirm.service_category} />
              <Detail label="Date Submitted" value={confirm.created_at ? new Date(confirm.created_at).toLocaleString() : "—"} />
              <Detail label="Current Status" value={confirm.status} />
            </div>
            <div className="mt-6"><div className="text-xs uppercase tracking-wide text-slate-400 mb-3">Status Timeline</div><StatusTimeline wo={confirm} /></div>
            <div className="flex gap-2 mt-6 flex-wrap">
              <Button data-testid="conf-view-details" onClick={() => navigate(`/app/wo/${confirm.id}`)} className="bg-blue-600 hover:bg-blue-700">View Details</Button>
              <Button data-testid="conf-new-request" variant="outline" onClick={() => { setConfirm(null); setF({ property_id: "", unit_number: "", service_category: "", problem_description: "", urgency: "Routine", requested_date: "", tenant_occupied: true, permission_to_enter: true, access_instructions: "", additional_notes: "" }); }}>New Request</Button>
            </div>
          </div>
        </div>
      ) : (
      <div className="max-w-2xl">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center"><PlusCircle className="w-5 h-5 text-white" /></div>
            <div><h2 className="font-head text-xl font-semibold text-slate-900">New Maintenance Request</h2><p className="text-sm text-slate-500">We'll review and dispatch a contractor.</p></div>
          </div>
          {props.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-slate-500">Add a property first.</p>
              <Button className="mt-4 bg-blue-600" onClick={() => navigate("/app/client/properties")}>Go to Properties</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Property</Label>
                <Select value={f.property_id} onValueChange={set("property_id")}>
                  <SelectTrigger data-testid="req-property" className="mt-1.5"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.city}, {p.state}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Unit Number</Label><Input data-testid="req-unit" value={f.unit_number} onChange={set("unit_number")} className="mt-1.5" /></div>
                <div>
                  <Label>Service Category</Label>
                  <Select value={f.service_category} onValueChange={set("service_category")}>
                    <SelectTrigger data-testid="req-category" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-64">{SERVICE_CATEGORIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Problem Description</Label><Textarea data-testid="req-description" required value={f.problem_description} onChange={set("problem_description")} className="mt-1.5" rows={3} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Urgency</Label>
                  <Select value={f.urgency} onValueChange={set("urgency")}>
                    <SelectTrigger data-testid="req-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{URGENCIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Requested Date</Label><Input data-testid="req-date" type="date" value={f.requested_date} onChange={set("requested_date")} className="mt-1.5" /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <Label className="mb-0">Tenant Occupied?</Label>
                <Switch data-testid="req-occupied" checked={f.tenant_occupied} onCheckedChange={(v) => setF({ ...f, tenant_occupied: v })} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <Label className="mb-0">Permission to Enter?</Label>
                <Switch data-testid="req-permission" checked={f.permission_to_enter} onCheckedChange={(v) => setF({ ...f, permission_to_enter: v })} />
              </div>
              <div><Label>Access Instructions</Label><Textarea data-testid="req-access" value={f.access_instructions} onChange={set("access_instructions")} className="mt-1.5" /></div>
              <div><Label>Additional Notes</Label><Textarea data-testid="req-notes" value={f.additional_notes} onChange={set("additional_notes")} className="mt-1.5" /></div>
              <Button data-testid="req-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}</Button>
            </form>
          )}
        </div>
      </div>
      )}
    </DashboardLayout>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-800 font-medium text-right">{value || "—"}</span>
  </div>
);
